"use client";

/**
 * ProgressiveBuildingState (2026-08-19) - the clean "we're building your
 * comparison" state for progressive search.
 *
 * Fix 1 (2026-08-19): the discovered shops are known ~6s in (the `targets`
 * event), long before their prices arrive. So the moment we have real shop names
 * we render THEM here - slim rows "JB Hi-Fi ... checking price" - instead of
 * generic placeholders, so the shopper watches actual shops being checked from
 * second ~6, not a blank screen for 30s. A shop with no name yet (the brief
 * pre-`targets` discovery window) falls back to a shimmer bar.
 *
 * Alignment fix (2026-08-19): the pending rows now mirror the REAL compact
 * ComparisonRow card geometry so they read as one continuous list with the
 * loaded rows above, not a stray misaligned block:
 *   - same outer padding incl. the desktop override (px-4 py-3.5
 *     sm:px-[22px] sm:py-[18px]) - the missing sm: padding was pushing the
 *     shop name 6px left of the loaded rows.
 *   - same radius (rounded-2xl sm:rounded-[14px]).
 *   - no leading icon - the real rows put the retailer name at the left edge
 *     (there is no retailer logo anywhere in ComparisonRow), so the old h-8
 *     icon box shoved the name ~44px right of the loaded names.
 *   - same vertical rhythm as the loaded list (mt-3, space-y-3).
 *   - the retailer name uses the compact loading name style
 *     (text-sm font-medium tracking-tight text-[var(--cmp-fg-2)]) so a pending
 *     row is a dimmer, price-pending twin of a loaded row.
 * The old centered "Checking prices across N retailers..." caption is dropped
 * per the owner (not needed); the string is kept only as the section's
 * aria-label so screen readers still hear progress.
 *
 * Real loaded rows render ABOVE this via the normal ComparisonRow path; this
 * stands in only for the shops still being priced, so no empty boxes ever show.
 * Only rendered by ComparisonStream when PROGRESSIVE_SEARCH_ENABLED is on and the
 * stream is still running. Purely cosmetic - no data, no state.
 */

import { motion, useReducedMotion } from "framer-motion";

import { SLIM_SEARCH_HERO_ENABLED } from "@/lib/flags";

export function ProgressiveBuildingState({
  label,
  shops,
}: {
  /** Progress text, kept for the section's aria-label only (no visible caption). */
  label: string;
  /** Prettified names of shops discovered but not yet priced. Empty/undefined =
   * the brief pre-discovery window, where we show shimmer bars instead. */
  shops?: string[];
}) {
  const reduceMotion = useReducedMotion();
  const hasNames = !!shops && shops.length > 0;
  const rows: Array<string | null> = hasNames
    ? shops!
    : Array.from({ length: 4 }, () => null);

  // 2026-08-28 (owner-approved Idea 3, gated with the slim hero): named rows
  // cascade in one after another (~80ms stagger) so the list visibly BUILDS
  // instead of popping in as a finished block. Mount-only (keys are the shop
  // names, so re-renders never replay it); reduced motion = no stagger.
  const cascade = SLIM_SEARCH_HERO_ENABLED && hasNames && !reduceMotion;

  return (
    <section className="mt-3 space-y-3" aria-live="polite" aria-label={label}>
      {rows.map((name, i) => (
        // 2026-08-28 Option 1 ("price slot filling", owner-approved): each
        // pending shop renders as the ROW'S OWN loading card - the exact
        // shell + shimmer vocabulary ComparisonRow's LoadingShimmer uses
        // (border-border bg-secondary, compact geometry, bg-border/40
        // pulsing bars) - with a price-shaped shimmer where the price will
        // land. The checking state and the loaded row are the same object
        // at two moments in time; no third design language.
        <motion.div
          key={name ?? `sk-${i}`}
          initial={cascade ? { opacity: 0, y: 6 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1],
            delay: cascade ? Math.min(i * 0.08, 0.8) : 0,
          }}
          className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-secondary px-4 py-3.5 sm:rounded-[14px] sm:px-[22px] sm:py-[18px]"
          aria-label={name ? `Checking price at ${name}` : "Checking price"}
          aria-busy="true"
        >
          <div className="min-w-0">
            {name ? (
              <span className="block truncate text-sm font-medium tracking-tight text-foreground">
                {name}
              </span>
            ) : (
              <div className="h-4 w-32 max-w-[45vw] animate-pulse rounded bg-border/40" />
            )}
            {/* The canonical loading-card micro-label (ComparisonRow's
                "checking now…" vocabulary: xs small-caps, tracked, muted). */}
            <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">
              checking price&hellip;
            </p>
          </div>
          {/* The price slot: same pulsing bar the row's own loading state
              renders where the price appears. */}
          <div className="h-7 w-24 shrink-0 animate-pulse rounded bg-border/40" />
        </motion.div>
      ))}
    </section>
  );
}
